import { useCallback, useMemo } from "react";
import { View } from "react-native";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import { FullPublicPhotoFragmentFragment } from "src/api/graphql/__generated__/graphql";
import {
	DefaultShareComponentLookup,
	SharePayloadProp,
	ShareWrapper,
	ValidShareOption,
} from "src/components/ShareMenu";
import parseV0TemplateUrl from "src/helpers/parseV0TemplateUrl";
import { useInsideModal } from "src/modals";
import { paddingLarge } from "src/styles/spacing";

const PhotoPublicPreviewFooter = ({ photo }: { photo: FullPublicPhotoFragmentFragment }) => {
	const imageShareProps = useMemo<SharePayloadProp | null>(() => {
		const imageUrl = parseV0TemplateUrl({
			readUrl: photo.readUrl,
			direct: "original",
		});
		const videoUrl = parseV0TemplateUrl({
			readUrl: photo.readUrl,
			direct: "original-video",
		});
		const successAmplitude = {
			eventName: "photoPublicPreview_shareAction",
			eventProperties: {
				photoId: photo.photoId,
				ownerId: photo.ownerId,
				isTopPhoto: photo.isTopPhoto,
				variant: photo.variant,
			},
		};
		// Safe to do since parseV0TemplateUrl is based entirely on the same v0TemplateUrl existing
		if (!imageUrl || !videoUrl) return null;

		const { variant } = photo;
		switch (variant) {
			case PhotoVariant.LivePhoto:
				return {
					data: [
						{
							type: "LivePhoto",
							url: imageUrl,
							videoUrl: videoUrl,
							successAmplitude,
						},
					],
				} satisfies SharePayloadProp;
			case PhotoVariant.Video:
				return {
					data: [
						{
							type: "Video",
							videoUrl,
							successAmplitude,
						},
					],
				} satisfies SharePayloadProp;
			default:
				return {
					data: [
						{
							type: "Image",
							url: imageUrl,
							successAmplitude,
						},
					],
				} satisfies SharePayloadProp;
		}
	}, [photo]);

	const renderOptions = useCallback((options: ValidShareOption[]) => {
		const Content = options.map((option) => {
			const Component = DefaultShareComponentLookup[option.key];
			return <Component key={option.key} data={option.data as any} />;
		});
		return Content;
	}, []);

	const renderImageItems = useCallback(
		(options: ValidShareOption[]) => {
			const filteredOptions = options.filter(
				(option) => option.key === "ShareDownload" || option.key === "ShareInstagramStory",
			);
			return renderOptions(filteredOptions);
		},
		[renderOptions],
	);
	const { closeSelf } = useInsideModal();

	return (
		<View
			testID="ShareMenu"
			pointerEvents="box-none"
			style={{
				flexDirection: "row",
				columnGap: 60,
				alignItems: "center",
				justifyContent: "center",
				marginVertical: paddingLarge,
			}}
		>
			{imageShareProps && (
				<ShareWrapper
					onComplete={() => {
						closeSelf();
					}}
					{...imageShareProps}
					displayIconText={false}
					renderItems={renderImageItems}
				/>
			)}
		</View>
	);
};
export default PhotoPublicPreviewFooter;
